// ./src/utils/fileHelpers.ts
/**
 * Shared utility functions for file handling across the application.
 * Used by file-related components like FilePreviewCard, AttachedFiles, FilePickerDrawer, etc.
 */

/**
 * File category types for display and handling logic
 */
export type FileCategory =
  | "image"
  | "pdf"
  | "video"
  | "audio"
  | "document"
  | "spreadsheet"
  | "presentation"
  | "archive"
  | "other";

/**
 * Common image MIME types that can be previewed in the browser
 */
const PREVIEWABLE_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
  "image/bmp",
];

/**
 * MIME type patterns for categorization
 */
const MIME_TYPE_PATTERNS: Record<FileCategory, RegExp> = {
  image: /^image\//,
  pdf: /^application\/pdf$/,
  video: /^video\//,
  audio: /^audio\//,
  document:
    /^(application\/(msword|vnd\.openxmlformats-officedocument\.wordprocessingml|vnd\.oasis\.opendocument\.text)|text\/(plain|rtf|markdown))/,
  spreadsheet:
    /^application\/(vnd\.ms-excel|vnd\.openxmlformats-officedocument\.spreadsheetml|vnd\.oasis\.opendocument\.spreadsheet)|text\/csv/,
  presentation:
    /^application\/(vnd\.ms-powerpoint|vnd\.openxmlformats-officedocument\.presentationml|vnd\.oasis\.opendocument\.presentation)/,
  archive:
    /^application\/(zip|x-rar|x-7z-compressed|x-tar|gzip|x-bzip2|x-zip-compressed)/,
  other: /.*/, // Fallback pattern
};

/**
 * Icon names mapped to file categories (using Lucide icon names)
 */
const CATEGORY_ICONS: Record<FileCategory, string> = {
  image: "Image",
  pdf: "FileText",
  video: "Video",
  audio: "Music",
  document: "FileText",
  spreadsheet: "Table",
  presentation: "Presentation",
  archive: "Archive",
  other: "File",
};

/**
 * Determines the file category based on MIME type
 *
 * @param mimeType - The MIME type of the file
 * @returns The file category
 *
 * @example
 * getFileCategory('image/jpeg') // returns 'image'
 * getFileCategory('application/pdf') // returns 'pdf'
 * getFileCategory('text/csv') // returns 'spreadsheet'
 */
export function getFileCategory(
  mimeType: string | null | undefined
): FileCategory {
  if (!mimeType) {
    return "other";
  }

  const normalizedMimeType = mimeType.toLowerCase().trim();

  // Check each pattern in order (excluding 'other' which is the fallback)
  const categories: FileCategory[] = [
    "image",
    "pdf",
    "video",
    "audio",
    "spreadsheet", // Check before document since CSV is text/
    "presentation",
    "document",
    "archive",
  ];

  for (const category of categories) {
    if (MIME_TYPE_PATTERNS[category].test(normalizedMimeType)) {
      return category;
    }
  }

  return "other";
}

/**
 * Gets the appropriate Lucide icon name for a file based on its MIME type
 *
 * @param mimeType - The MIME type of the file
 * @returns The Lucide icon name
 *
 * @example
 * getFileIcon('image/png') // returns 'Image'
 * getFileIcon('application/pdf') // returns 'FileText'
 */
export function getFileIcon(mimeType: string | null | undefined): string {
  const category = getFileCategory(mimeType);
  return CATEGORY_ICONS[category];
}

/**
 * Formats a file size in bytes to a human-readable string
 *
 * @param bytes - The file size in bytes
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted file size string
 *
 * @example
 * formatFileSize(1024) // returns '1 KB'
 * formatFileSize(1536000) // returns '1.46 MB'
 * formatFileSize(0) // returns '0 Bytes'
 */
export function formatFileSize(
  bytes: number | null | undefined,
  decimals: number = 2
): string {
  if (bytes === null || bytes === undefined || bytes === 0) {
    return "0 Bytes";
  }

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(Math.abs(bytes)) / Math.log(k));
  const index = Math.min(i, sizes.length - 1);

  return `${parseFloat((bytes / Math.pow(k, index)).toFixed(decimals))} ${
    sizes[index]
  }`;
}

/**
 * Checks if a file can be previewed as an image in the browser
 *
 * @param mimeType - The MIME type of the file
 * @returns True if the file is a previewable image
 *
 * @example
 * isPreviewableImage('image/jpeg') // returns true
 * isPreviewableImage('image/svg+xml') // returns true
 * isPreviewableImage('application/pdf') // returns false
 */
export function isPreviewableImage(
  mimeType: string | null | undefined
): boolean {
  if (!mimeType) {
    return false;
  }

  return PREVIEWABLE_IMAGE_TYPES.includes(mimeType.toLowerCase().trim());
}

/**
 * Checks if a file is a PDF
 *
 * @param mimeType - The MIME type of the file
 * @returns True if the file is a PDF
 */
export function isPdf(mimeType: string | null | undefined): boolean {
  if (!mimeType) {
    return false;
  }

  return mimeType.toLowerCase().trim() === "application/pdf";
}

/**
 * Checks if a file is a video
 *
 * @param mimeType - The MIME type of the file
 * @returns True if the file is a video
 */
export function isVideo(mimeType: string | null | undefined): boolean {
  if (!mimeType) {
    return false;
  }

  return mimeType.toLowerCase().trim().startsWith("video/");
}

/**
 * Checks if a file can be opened/viewed in a new browser tab
 * (images, PDFs, videos, and some text files)
 *
 * @param mimeType - The MIME type of the file
 * @returns True if the file can be opened in a new tab
 */
export function canOpenInBrowser(mimeType: string | null | undefined): boolean {
  if (!mimeType) {
    return false;
  }

  const normalizedMimeType = mimeType.toLowerCase().trim();

  return (
    isPreviewableImage(normalizedMimeType) ||
    isPdf(normalizedMimeType) ||
    isVideo(normalizedMimeType) ||
    normalizedMimeType.startsWith("text/") ||
    normalizedMimeType === "application/json"
  );
}

/**
 * Gets a file extension from a filename
 *
 * @param filename - The filename
 * @returns The file extension (lowercase, without dot) or empty string
 *
 * @example
 * getFileExtension('document.pdf') // returns 'pdf'
 * getFileExtension('image.PNG') // returns 'png'
 * getFileExtension('noextension') // returns ''
 */
export function getFileExtension(filename: string | null | undefined): string {
  if (!filename) {
    return "";
  }

  const lastDotIndex = filename.lastIndexOf(".");
  if (lastDotIndex === -1 || lastDotIndex === filename.length - 1) {
    return "";
  }

  return filename.slice(lastDotIndex + 1).toLowerCase();
}

/**
 * Truncates a filename while preserving the extension
 *
 * @param filename - The filename to truncate
 * @param maxLength - Maximum length of the truncated name (default: 20)
 * @returns Truncated filename with extension preserved
 *
 * @example
 * truncateFilename('very-long-document-name.pdf', 15) // returns 'very-long-d...pdf'
 * truncateFilename('short.pdf', 20) // returns 'short.pdf'
 */
export function truncateFilename(
  filename: string | null | undefined,
  maxLength: number = 20
): string {
  if (!filename) {
    return "";
  }

  if (filename.length <= maxLength) {
    return filename;
  }

  const extension = getFileExtension(filename);
  const nameWithoutExt = extension
    ? filename.slice(0, filename.lastIndexOf("."))
    : filename;

  // Calculate how much of the name we can keep
  // Account for "..." (3 chars) and extension with dot
  const extensionPart = extension ? `.${extension}` : "";
  const availableLength = maxLength - 3 - extensionPart.length;

  if (availableLength <= 0) {
    // If max length is too short, just truncate everything
    return `${filename.slice(0, maxLength - 3)}...`;
  }

  return `${nameWithoutExt.slice(0, availableLength)}...${extensionPart}`;
}

/**
 * Triggers a file download in the browser
 *
 * @param url - The URL of the file to download
 * @param filename - The filename to save as (optional)
 */
export function downloadFile(url: string, filename?: string): void {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || "";
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Opens a file in a new browser tab
 *
 * @param url - The URL of the file to open
 */
export function openInNewTab(url: string): void {
  window.open(url, "_blank", "noopener,noreferrer");
}
